import { ApiPromise, WsProvider } from '@polkadot/api';
import { useState, useEffect } from 'react';

// Default endpoints for Substrate nodes
export const SUBSTRATE_ENDPOINTS = {
  // TFChain endpoints
  tfchain_dev: 'wss://tfchain.dev.grid.tf',
  tfchain_qa: 'wss://tfchain.qa.grid.tf',
  tfchain_test: 'wss://tfchain.test.grid.tf',
  tfchain_prod: 'wss://tfchain.grid.tf',
};

// Create a singleton API instance
let api: ApiPromise | null = null;

export const connectToSubstrate = async (endpoint: string): Promise<ApiPromise> => {
  if (api) {
    try {
      await api.disconnect();
    } catch (error) {
      console.error('Error disconnecting from previous endpoint:', error);
    }
    api = null;
  }

  // Create provider with better reconnection settings
  const provider = new WsProvider(endpoint);
  
  try {
    api = await ApiPromise.create({ 
      provider,
      throwOnConnect: true, // Throw error if initial connection fails
      noInitWarn: false     // Show warnings during initialization
    });
    return api;
  } catch (error) {
    console.error(`Failed to connect to endpoint ${endpoint}:`, error);
    throw error;
  }
};

export const getApi = (): ApiPromise | null => {
  return api;
};

// Custom hook for using the Substrate API
export const useSubstrateApi = (endpoint: string = SUBSTRATE_ENDPOINTS.tfchain_prod) => {
  const [api, setApi] = useState<ApiPromise | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    const connect = async () => {
      try {
        const api = await connectToSubstrate(endpoint);
        
        // Set up event handlers
        const connectedCb = () => {
          setIsConnected(true);
          setError(null);
        };
        
        const disconnectedCb = () => {
          setIsConnected(false);
        };
        
        const errorCb = (error: Error) => {
          setError(error);
        };
        
        // Subscribe to connection status
        api.on('connected', connectedCb);
        api.on('disconnected', disconnectedCb);
        api.on('error', errorCb);
        
        // Store unsubscribe function that removes all listeners
        unsubscribe = () => {
          api.off('connected', connectedCb);
          api.off('disconnected', disconnectedCb);
          api.off('error', errorCb);
        };
        
        setApi(api);
        setIsConnected(true);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsConnected(false);
      }
    };

    connect();

    return () => {
      // Clean up event listeners
      if (unsubscribe) {
        unsubscribe();
      }
      
      // Disconnect from the API
      if (api) {
        api.disconnect().catch(console.error);
      }
    };
  }, [endpoint]);

  return { api, isConnected, error };
};
